import { useTranslation } from 'next-i18next';
import type { Material } from '@igus/iglidur-free-form-hub-interfaces';
import { FilterType, freeFormHubFilterKeys } from '../../types';
import { FilterWrapper, type FilterWrapperProps } from '../filterWrapper/filterWrapper';
import { useFreeFormHubFilterState } from '../../hooks/useUrlFilterState';
import { useFreeFormHubInputValidator } from '../../hooks/inputValidator';
import { useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { CompareMaterialsModal } from '@hub/free-form-hub/free-form-hub-result/product-filters/components/compareMaterialsModal/CompareMaterialsModal';
import { ffhEventCategory, TrackedFfhEventActions } from '@hub/free-form-hub/tracking.config';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { SUPPORTED_IGLIDE_IBH_LOCALES } from '@hub/libs/bearing-hub.helper';
import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';

type MaterialSectionProps = {
  materials: Material[];
  setActiveIglideStandardModal: (state: boolean) => void;
  activeIglideStandardModal: boolean;
};

export const MaterialSection = ({
  materials,
  activeIglideStandardModal,
  setActiveIglideStandardModal,
}: MaterialSectionProps) => {
  const { t } = useTranslation(['category/productFilter', 'common']);
  const { CustomEvent } = usePiwikPro();
  const router = useRouter();
  const locale = getLocale(router);
  const multiselectValidator = useFreeFormHubInputValidator(FilterType.AUTOCOMPLETE);
  const checkboxValidator = useFreeFormHubInputValidator(FilterType.CHECKBOX);
  const [values, setValue] = useFreeFormHubFilterState<FilterType.AUTOCOMPLETE>(
    freeFormHubFilterKeys.MATERIAL,
    multiselectValidator
  );
  const [pfasValue, setPfasValue] = useFreeFormHubFilterState<FilterType.CHECKBOX>(
    freeFormHubFilterKeys.PFAS_FREE,
    checkboxValidator
  );
  const [ptfeValue, setPtfeValue] = useFreeFormHubFilterState<FilterType.CHECKBOX>(
    freeFormHubFilterKeys.PTFE_FREE,
    checkboxValidator
  );
  const [useUsMaterial, setUseUsMaterial] = useFreeFormHubFilterState<FilterType.CHECKBOX>(
    freeFormHubFilterKeys.US_ONLY_MATERIALS,
    checkboxValidator
  );
  const [showCompareMaterials, setShowCompareMaterials] = useState(false);
  const getUniqueOptions = () => {
    const uniqueMaterials = new Set();
    return materials
      .filter(item => {
        if (uniqueMaterials.has(item.name)) {
          return false;
        }
        uniqueMaterials.add(item.name);
        return true;
      })
      .map(item => ({
        label: item.name,
        value: item.name,
      }));
  };
  const couldResetMaterialValues = values?.length > 0;
  const options = getUniqueOptions();
  const selectValue = useMemo(
    () => values?.map(val => options.find(opt => opt.value === val)).filter(opt => opt !== undefined) ?? [],
    [values, options]
  );

  const pfasSectionConfig: FilterWrapperProps[] = [
    {
      type: FilterType.CHECKBOX,
      props: {
        key: freeFormHubFilterKeys.PFAS_FREE,
        id: freeFormHubFilterKeys.PFAS_FREE,
        label: t(`FILTERS.LABELS.PFAS_FREE`, { ns: 'free-form-hub/freeFormHub' }),
        disabled: false,
        value: pfasValue ?? false,
        onChange: newValue => {
          setPfasValue(newValue === true ? true : undefined);
        },
      },
    },
    {
      type: FilterType.CHECKBOX,
      props: {
        key: freeFormHubFilterKeys.PTFE_FREE,
        id: freeFormHubFilterKeys.PTFE_FREE,
        label: t(`FILTERS.LABELS.PTFE_FREE`, { ns: 'free-form-hub/freeFormHub' }),
        disabled: false,
        value: ptfeValue ?? false,
        onChange: newValue => {
          setPtfeValue(newValue === true ? true : undefined);
        },
      },
    },
  ];

  const materialSectionConfig: FilterWrapperProps[] = [
    {
      type: FilterType.AUTOCOMPLETE,
      props: {
        key: freeFormHubFilterKeys.MATERIAL,
        id: freeFormHubFilterKeys.MATERIAL,
        placeholder: t(`FILTERS.PLACEHOLDER.MATERIAL`, { ns: 'free-form-hub/freeFormHub' }),
        options,
        filterOptions: (option: { label: string; value: string }, materialSearchTerm: string) => {
          const sanitize = (label: string): string => label.replace('®', '');
          return sanitize(option.label).toLowerCase().includes(sanitize(materialSearchTerm.trim()).toLowerCase());
        },
        value: selectValue,
        isMulti: true,
        onChange: (changeValue: string[]) => {
          if (changeValue.length) {
            CustomEvent.trackEvent(
              ffhEventCategory,
              TrackedFfhEventActions.filterMaterialSelect,
              JSON.stringify(changeValue)
            );
          }
          if (!changeValue?.length) {
            setValue(undefined);
            return;
          }

          if (!Array.isArray(changeValue)) {
            setValue([changeValue]);
            return;
          }

          setValue(changeValue ?? undefined);
        },
      },
    },
    SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) && {
      type: FilterType.CHECKBOX,
      props: {
        disabled: !!ptfeValue,
        key: freeFormHubFilterKeys.PTFE_FREE,
        id: `filter_show_only_us_materials}${freeFormHubFilterKeys.PTFE_FREE}`,
        value: useUsMaterial,
        onChange: newValue => {
          setUseUsMaterial(newValue);
        },
        label: t(`FILTERS.LABELS.US_STANDARD_MATERIALS`, { ns: 'free-form-hub/freeFormHub' }),
      },
    },
  ];

  const extractFilter = (filter: FilterWrapperProps) => {
    const uuId = v4();
    return (
      <div className="mt-8" key={uuId}>
        <FilterWrapper type={filter.type} props={filter.props} />
      </div>
    );
  };

  return (
    <>
      {materialSectionConfig.map(filter => extractFilter(filter))}
      <button
        type="button"
        className="text-[#D66F00] font-medium my-4"
        data-testid={values?.length > 0 ? 'reset-material-filter' : 'compare-material'}
        onClick={() => {
          if (couldResetMaterialValues) {
            setValue(undefined);
          } else {
            CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.filterCompareMaterialsOpen);
            setShowCompareMaterials(true);
          }
        }}
      >
        {values?.length > 0
          ? t(`FILTERS.SECTIONS.RESET_MATERIAL_FILTER`, { ns: 'free-form-hub/freeFormHub' })
          : t(`FILTERS.SECTIONS.COMPARE_MATERIALS`, { ns: 'free-form-hub/freeFormHub' })}
      </button>
      <CompareMaterialsModal
        materials={materials}
        activeModal={showCompareMaterials}
        setActiveModal={setShowCompareMaterials}
      />
      {SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) && (
        <div>
          <button
            data-testid="iglide_standard"
            type="button"
            className="text-[#D66F00] space-x-3 font-medium"
            onClick={() => {
              setActiveIglideStandardModal(!activeIglideStandardModal);
            }}
          >
            <Icon size={7} color={IconColor.ORANGE} id={IconID.PDF_FILES} />
            <span>{t(`FILTERS.SECTIONS.IGLIDE_STANDARDS`, { ns: 'bearing-hub/bearingHub' })}</span>
          </button>
        </div>
      )}
      {pfasSectionConfig.map(filter => extractFilter(filter))}
    </>
  );
};
